import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wine, Skull, Sparkles, PenLine, ArrowRight, Mail, Clock, LogIn, User, Building2, Instagram, Youtube, Linkedin, Link as LinkIcon, Heart, Gift, Globe, Flame } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, startGoogleSignIn } from "@/lib/auth";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import HeroCinematic from "@/components/HeroCinematic";
import TransmissionIntro from "@/components/TransmissionIntro";
import ArenaHero from "@/components/ArenaHero";
import PromoBanner from "@/components/PromoBanner";

const heroImg =
  "https://images.unsplash.com/photo-1705169792059-24904742bf03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxjb2NrdGFpbCUyMG1hcnRpbmklMjBnbGFzcyUyMGRhcmslMjBzaGFkb3clMjByZWR8ZW58MHx8fHwxNzkxNzIyMTAwfDA&ixlib=rb-4.1.0&q=85";

const AMAZON_URL = "https://www.amazon.com/dp/B0HBWJTYGB?binding=paperback&ref_=saga_sdp_cft_mbl";

const useActiveSeason = () => {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/settings/homepage").then((r) => {
      if (alive) setConfig(r.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!config?.enabled) return { config, active: null };

  const seasons = config.seasons || [];
  // Admin override wins
  if (config.active_override_season_id) {
    const pinned = seasons.find((s) => s.id === config.active_override_season_id);
    if (pinned) return { config, active: pinned };
  }
  // Auto-pick by visitor's local date
  const now = new Date();
  const mmdd = `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const inRange = (s) => {
    const [a, b] = [s.start, s.end];
    // Both inclusive, wrap-around not needed since no season spans year end
    return mmdd >= a && mmdd <= b;
  };
  const matches = seasons.filter(inRange);
  if (matches.length === 0) return { config, active: null };
  // Highest priority wins; ties broken by shorter window (more specific)
  matches.sort((a, b) => (b.priority || 0) - (a.priority || 0));
  return { config, active: matches[0] };
};

const daysUntilMMDD = (targetMMDD) => {
  if (!targetMMDD) return null;
  const [tm, td] = targetMMDD.split("-").map((n) => parseInt(n, 10));
  const now = new Date();
  let target = new Date(now.getFullYear(), tm - 1, td);
  if (target < now) target = new Date(now.getFullYear() + 1, tm - 1, td);
  const diffMs = target - now;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

const SeasonBanner = ({ season }) => {
  if (!season) return null;
  const days = daysUntilMMDD(season.countdown_target);
  const label = season.countdown_label && days !== null && days >= 0
    ? season.countdown_label.replace("{days}", days)
    : "";
  const accent = season.accent_hex || "#8b0000";
  return (
    <div className="relative overflow-hidden border-b border-[#2d2424] py-3 text-center"
      style={{ background: `linear-gradient(90deg, ${accent}22, transparent, ${accent}22)` }}
      data-testid="season-banner">
      <div className="max-w-6xl mx-auto px-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
        <span className="text-[10px] tracking-[0.2em] uppercase" style={{ color: accent }}
          data-testid="season-label">
          · {season.label} ·
        </span>
        {label && <span className="text-[#f4f0ec]" data-testid="season-countdown">{label}</span>}
      </div>
    </div>
  );
};

const NovelTeaserModal = ({ onClose }) => (
  <div
    className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 overflow-y-auto"
    onClick={onClose}
    data-testid="novel-teaser-backdrop"
  >
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.25 }}
      className="card-noir p-8 max-w-xl w-full relative"
      onClick={(e) => e.stopPropagation()}
      data-testid="novel-teaser-modal"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-[#a39797] hover:text-[#f4f0ec] text-sm"
        aria-label="Close"
        data-testid="novel-teaser-close"
      >
        close
      </button>

      <div className="grid sm:grid-cols-[140px_1fr] gap-6 items-start">
        <div className="aspect-[2/3] rounded-md border-2 border-[#8b0000] bg-gradient-to-br from-[#2d2424] to-[#0a0908] flex flex-col items-center justify-center relative overflow-hidden shadow-[0_8px_24px_rgba(139,0,0,0.3)]">
          <div className="font-display text-5xl text-[#8b0000]">NB</div>
          <div className="font-hand text-[#d4af37] text-xs mt-2 px-2 text-center rotate-[-2deg]">Awakening</div>
          <div className="absolute bottom-2 left-2 right-2 text-center">
            <div className="text-[8px] tracking-[0.2em] text-[#a39797]">BOOK ONE</div>
          </div>
        </div>

        <div>
          <div className="font-hand text-[#d4af37] rotate-[-1deg] text-lg mb-1">from the author</div>
          <div className="text-[10px] tracking-[0.2em] text-[#a39797] mb-1">NIGHTBLOOD CHRONICLES · BOOK ONE</div>
          <h2 className="font-display text-3xl mb-3 leading-tight">Awakening</h2>
          <p className="text-[#a39797] leading-relaxed mb-2 text-sm">
            A gothic-suspense debut about a woman who wakes with no memory and a bloodline that refuses to stay buried.
          </p>
          <p className="text-[#a39797] leading-relaxed text-sm">
            The same hand that writes your dinner-party murders — before it learned to hide the body in a kit.
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 pt-5 border-t border-[#2d2424]">
        <div className="text-xs text-[#a39797]">Paperback · Available on Amazon</div>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-ghost text-sm" data-testid="novel-teaser-not-now">Not now</button>
          <a
            href={AMAZON_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className="btn-primary text-sm"
            data-testid="novel-teaser-amazon-cta"
          >
            <Sparkles size={14} /> Read on Amazon
          </a>
        </div>
      </div>
    </motion.div>
  </div>
);

const SocialLink = ({ href, label, children, testid }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" title={label} aria-label={label}
    data-testid={testid}
    className="w-10 h-10 rounded-full border border-[#2d2424] flex items-center justify-center text-[#a39797] hover:text-[#d4af37] hover:border-[#d4af37] transition-colors">
    {children}
  </a>
);

const CaseCard = ({ c, index, onComingSoon, playtestPromo }) => {
  const palettes = [
    { accent: "#8b0000", wash: "from-[#8b0000]/15 to-transparent" },
    { accent: "#d4af37", wash: "from-[#d4af37]/15 to-transparent" },
    { accent: "#b30000", wash: "from-[#b30000]/15 to-transparent" },
    { accent: "#8b0000", wash: "from-[#8b0000]/10 to-transparent" },
    { accent: "#d4af37", wash: "from-[#d4af37]/10 to-transparent" },
    { accent: "#b30000", wash: "from-[#b30000]/10 to-transparent" },
  ];
  const p = palettes[index % palettes.length];
  const isComingSoon = !!c.coming_soon;
  const showPlaytest = !!(playtestPromo && playtestPromo.applies_to?.includes(c.id));
  const copyPromo = () => {
    try {
      navigator.clipboard.writeText(playtestPromo.code);
      toast.success(`${playtestPromo.code} copied — paste it at Stripe checkout.`);
    } catch { /* no-op */ }
  };

  return (
    <motion.div
      className={`card-noir p-6 relative overflow-hidden flex flex-col ${isComingSoon ? "opacity-90" : ""}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
      data-testid={`case-card-${c.id}`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${p.wash} pointer-events-none`} />
      {showPlaytest && !isComingSoon && (
        <div className="absolute top-3 right-3 z-10" data-testid={`playtest-badge-${c.id}`}>
          <button
            onClick={copyPromo}
            className="text-[10px] tracking-widest px-2 py-1 rounded-full border border-[#8b0000] text-[#f4f0ec] bg-[#8b0000]/80 hover:bg-[#8b0000] transition-colors cursor-pointer flex items-center gap-1"
            title={`${playtestPromo.remaining} of ${playtestPromo.max_redemptions} spots left`}
          >
            🎃 {playtestPromo.code} · {playtestPromo.percent_off}% OFF
          </button>
        </div>
      )}
      {isComingSoon && (
        <div className="absolute top-3 right-3 z-10">
          <span className="text-[10px] tracking-widest px-2 py-1 rounded-full border border-[#d4af37] text-[#d4af37] bg-[#0a0908]/60">
            <Clock size={10} className="inline mr-1" /> COMING SOON
          </span>
        </div>
      )}
      <div className="relative flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div className="font-hand text-[#d4af37] rotate-[-2deg] text-xl">case file</div>
          <div className="text-xs tracking-widest text-[#a39797]">#{c.number}</div>
        </div>
        <div className="font-display text-3xl leading-tight mb-2" style={{ color: p.accent }}>
          {c.title}
        </div>
        <div className="font-hand text-[#f4f0ec] rotate-[-1deg] text-lg mb-4">{c.tagline}</div>
        <div className="text-sm text-[#a39797] leading-relaxed flex-1">{c.description}</div>
        <div className="flex flex-wrap gap-2 mt-4">
          {c.flavor_tags.map((t) => (
            <span key={t} className="text-[10px] tracking-widest uppercase px-2 py-1 rounded-full border border-[#2d2424] text-[#a39797]">
              {t}
            </span>
          ))}
        </div>
        {c.props_note && (
          <div className="mt-3 text-[11px] text-[#d4af37] italic flex items-center gap-1" data-testid={`props-note-${c.id}`}>
            ✱ {c.props_note}
          </div>
        )}
        {showPlaytest && !isComingSoon && (
          <div className="mt-3 text-[11px] text-[#f4f0ec] flex items-center gap-2 p-2 border border-[#8b0000] bg-[#8b0000]/10 rounded"
               data-testid={`playtest-tagline-${c.id}`}>
            <span className="text-[#8b0000] font-bold">PLAYTESTER</span>
            <span>{playtestPromo.remaining}/{playtestPromo.max_redemptions} spots at {playtestPromo.percent_off}% off — drop notes after the game</span>
          </div>
        )}
        <div className="flex items-center justify-between mt-6 pt-5 border-t border-[#2d2424]">
          <div>
            <div className="font-display text-2xl text-[#f4f0ec]">{c.price_label}</div>
            <div className="text-xs text-[#a39797]">
              {c.group_sizes.length === 1 ? `exactly ${c.group_sizes[0]} players` : `groups of ${c.group_sizes.join("–")}`}
            </div>
          </div>
          {isComingSoon ? (
            <button className="btn-ghost text-sm" onClick={() => onComingSoon(c)}
              data-testid={`notify-case-${c.id}`}>
              <Mail size={14} /> Notify me
            </button>
          ) : (
            <Link to={`/checkout?case=${c.id}`} className="btn-primary text-sm"
              data-testid={`start-case-${c.id}`}>
              Start this case <ArrowRight size={16} />
            </Link>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const NotifyModal = ({ caseInfo, onClose }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { email, case_interest: caseInfo.id, source: "case_vault_notify" });
      setDone(true);
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-noir p-8 max-w-md w-full relative" onClick={(e) => e.stopPropagation()} data-testid="notify-modal">
        {done ? (
          <>
            <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-2">on the list</div>
            <h2 className="font-display text-3xl mb-3">You're in.</h2>
            <p className="text-[#a39797] mb-6">We'll email you the moment <strong className="text-[#f4f0ec]">{caseInfo.title}</strong> drops.</p>
            <button className="btn-primary" onClick={onClose}>Nice</button>
          </>
        ) : (
          <>
            <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-2">first in line</div>
            <h2 className="font-display text-3xl mb-2">{caseInfo.title}</h2>
            <p className="text-[#a39797] mb-6">{caseInfo.tagline}</p>
            <form onSubmit={submit} className="space-y-4">
              <input type="email" required className="input-noir" placeholder="you@example.com"
                value={email} onChange={(e) => setEmail(e.target.value)} data-testid="notify-email" />
              <div className="flex gap-2">
                <button type="button" className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
                <button type="submit" disabled={loading} className="btn-primary text-sm" data-testid="notify-submit">
                  {loading ? "Adding..." : "Notify me"}
                </button>
              </div>
              <div className="text-xs text-[#a39797]">We'll only email when the case drops. No spam, ever.</div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const TeamCard = ({ card, index, onPlan }) => {
  const palettes = [
    { accent: "#d4af37", wash: "from-[#d4af37]/15 to-transparent" },
    { accent: "#8b0000", wash: "from-[#8b0000]/15 to-transparent" },
    { accent: "#b30000", wash: "from-[#b30000]/15 to-transparent" },
  ];
  const p = palettes[index % palettes.length];
  return (
    <motion.div className="card-noir p-6 relative overflow-hidden flex flex-col"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
      data-testid={`team-card-${card.id}`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${p.wash} pointer-events-none`} />
      <div className="relative flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div className="font-hand text-[#d4af37] rotate-[-2deg] text-xl">team file</div>
          <div className="text-xs tracking-widest text-[#a39797]">#{card.number}</div>
        </div>
        <div className="font-display text-2xl leading-tight mb-2" style={{ color: p.accent }}>
          {card.title}
        </div>
        <div className="font-hand text-[#f4f0ec] rotate-[-1deg] text-base mb-4">{card.tagline}</div>
        <div className="text-sm text-[#a39797] leading-relaxed flex-1">{card.description}</div>
        {card.limit_note && (
          <div className="mt-3 text-[11px] text-[#d4af37] italic leading-relaxed">
            Note: {card.limit_note}
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-4">
          {card.flavor_tags.map((t) => (
            <span key={t} className="text-[10px] tracking-widest uppercase px-2 py-1 rounded-full border border-[#2d2424] text-[#a39797]">
              {t}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between mt-6 pt-5 border-t border-[#2d2424]">
          <div>
            <div className="font-display text-lg text-[#f4f0ec]">{card.price_label}</div>
            <div className="text-xs text-[#a39797]">teams of {card.team_size_label}</div>
          </div>
          <button onClick={() => onPlan(card)} className="btn-primary text-sm"
            data-testid={`plan-event-${card.id}`}>
            {card.cta} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const InquiryModal = ({ initialParty, onClose }) => {
  const [form, setForm] = useState({
    party: initialParty || "halloween",
    company: "",
    contact_name: "",
    work_email: "",
    role: "",
    people_count: 30,
    preferred_date: "",
    format: "either",
    package: "",
    personalize: "not_sure",
    message: "",
    agree_contact: true,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agree_contact) {
      toast.error("Please agree to be contacted about this request.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/business/inquiry", { ...form, people_count: parseInt(form.people_count, 10) });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="card-noir p-8 max-w-2xl w-full relative my-10" onClick={(e) => e.stopPropagation()}
        data-testid="inquiry-modal">
        {done ? (
          <>
            <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-2">got it</div>
            <h2 className="font-display text-3xl mb-3">Request received.</h2>
            <p className="text-[#a39797] mb-6">Check your inbox — we sent a confirmation with a sample case link. We'll follow up with a quote within one business day.</p>
            <button className="btn-primary" onClick={onClose} data-testid="inquiry-close">Nice</button>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-1">plan a team event</div>
                <h2 className="font-display text-3xl">Tell us about your party.</h2>
              </div>
              <button onClick={onClose} className="text-[#a39797] hover:text-[#f4f0ec] text-sm" data-testid="inquiry-cancel">close</button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs tracking-widest text-[#a39797] mb-2">WHICH PARTY?</label>
                <select className="input-noir" value={form.party} onChange={(e) => update("party", e.target.value)}
                  data-testid="inquiry-party">
                  <option value="halloween">Halloween — The Haunted Office</option>
                  <option value="christmas">Christmas — Secret Santa Showdown</option>
                  <option value="new_years">New Year's — Countdown Killer</option>
                  <option value="summer">Summer / Fourth of July — Cookout Caper</option>
                  <option value="inventory">Inventory Day — Inventory Investigation</option>
                  <option value="team_pass">Team Pass (4 events / year)</option>
                  <option value="other">Something else</option>
                </select>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">COMPANY NAME</label>
                  <input className="input-noir" required value={form.company}
                    onChange={(e) => update("company", e.target.value)} data-testid="inquiry-company" />
                </div>
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">YOUR NAME</label>
                  <input className="input-noir" required value={form.contact_name}
                    onChange={(e) => update("contact_name", e.target.value)} data-testid="inquiry-contact-name" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">WORK EMAIL</label>
                  <input type="email" className="input-noir" required value={form.work_email}
                    onChange={(e) => update("work_email", e.target.value)} data-testid="inquiry-email" />
                </div>
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">YOUR ROLE (optional)</label>
                  <input className="input-noir" value={form.role}
                    onChange={(e) => update("role", e.target.value)} data-testid="inquiry-role" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">NUMBER OF PEOPLE</label>
                  <input type="number" min={1} max={5000} className="input-noir" required value={form.people_count}
                    onChange={(e) => update("people_count", e.target.value)} data-testid="inquiry-people-count" />
                </div>
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">PREFERRED DATE (optional)</label>
                  <input className="input-noir" placeholder="e.g. Oct 29 or 'not sure'" value={form.preferred_date}
                    onChange={(e) => update("preferred_date", e.target.value)} data-testid="inquiry-date" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">FORMAT</label>
                  <select className="input-noir" value={form.format} onChange={(e) => update("format", e.target.value)}
                    data-testid="inquiry-format">
                    <option value="in_person">In person</option>
                    <option value="virtual">Virtual</option>
                    <option value="either">Either</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">PACKAGE</label>
                  <select className="input-noir" value={form.package} onChange={(e) => update("package", e.target.value)}
                    data-testid="inquiry-package">
                    <option value="">Not sure</option>
                    <option value="starter">Starter — up to 15 ($249)</option>
                    <option value="team">Team — up to 30 ($449)</option>
                    <option value="department">Department — up to 60 ($799)</option>
                    <option value="company">Company — up to 150 ($1,499)</option>
                    <option value="custom">Custom / 150+ (quote)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs tracking-widest text-[#a39797] mb-2">ANYTHING ELSE?</label>
                <textarea className="input-noir min-h-[100px]" value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Off-limits topics, accessibility needs, logo, inside jokes…"
                  data-testid="inquiry-message" />
              </div>
              <label className="flex items-start gap-3 text-sm text-[#a39797] cursor-pointer">
                <input type="checkbox" required checked={form.agree_contact}
                  onChange={(e) => update("agree_contact", e.target.checked)}
                  className="mt-1 accent-[#8b0000]" data-testid="inquiry-agree" />
                <span>I agree to be contacted about this request.</span>
              </label>
              <div className="pt-2 flex items-center justify-end gap-3">
                <button type="button" className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
                <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50"
                  data-testid="inquiry-submit">
                  {loading ? "Sending…" : "Send request"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const Landing = () => {
  const [scrolled, setScrolled] = useState(false);
  const [cases, setCases] = useState([]);
  const [notifyCase, setNotifyCase] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterDone, setNewsletterDone] = useState(false);
  const [teamCards, setTeamCards] = useState([]);
  const [packages, setPackages] = useState([]);
  const [inquiryParty, setInquiryParty] = useState(null);
  const [showNovelTeaser, setShowNovelTeaser] = useState(false);
  const [playtestPromo, setPlaytestPromo] = useState(null);
  const [showHero, setShowHero] = useState(() => {
    try {
      // Preview mode via ?hero=preview — ignores sessionStorage so you can
      // iterate on timing/beats without burning the once-per-session flag.
      if (typeof window !== "undefined") {
        const q = new URLSearchParams(window.location.search);
        if (q.get("hero") === "preview") return true;
      }
      return !sessionStorage.getItem("hero_seen_v1");
    } catch { return true; }
  });
  const { user } = useAuth();
  const { active: activeSeason } = useActiveSeason();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  useEffect(() => {
    api.get("/cases").then((r) => setCases(r.data.cases)).catch(() => {});
    api.get("/business/products").then((r) => {
      setTeamCards(r.data.team_cards);
      setPackages(r.data.packages);
    }).catch(() => {});
    api.get("/playtest/horror-promo").then((r) => {
      if (r.data?.active && r.data?.remaining > 0) setPlaytestPromo(r.data);
    }).catch(() => {});
    // Capture ?ref=<code> for referral attribution at checkout
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get("ref") || "").trim().toUpperCase();
    if (ref) {
      localStorage.setItem("wasntme_ref", ref);
      api.post("/events", { event: "ref_code_viewed", ref_code: ref }).catch(() => {});
    }
    api.post("/events", { event: "page_view", props: { path: "/" } }).catch(() => {});
  }, []);

  const submitNewsletter = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { email: newsletterEmail, case_interest: "", source: "newsletter_footer" });
      setNewsletterDone(true);
      toast.success("You're on the list.");
    } catch {
      toast.error("Something went wrong");
    }
  };

  return (
    <div className="grain min-h-screen relative" data-testid="landing-page">
      {/* Sitewide promo banner — Early Black Friday → Cyber Monday phases */}
      <PromoBanner />

      {/* INCOMING TRANSMISSION — brand entry hook, one-per-browser (localStorage) */}
      <TransmissionIntro />

      {/* Cinematic hero — first thing a new visitor sees, one-per-session */}
      {showHero && <HeroCinematic onDismiss={() => setShowHero(false)} />}

      {/* GMN hype banner — Dec 31 Global Mystery Night */}
      <Link to="/night" data-testid="gmn-banner"
        className="block fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-[#8b0000] via-[#5a0000] to-[#8b0000] text-center text-xs sm:text-sm text-[#f8f3e4] py-2 px-4 border-b border-[#5a0000] hover:brightness-110 transition-all">
        <Globe size={12} className="inline-block mr-2 -mt-0.5" />
        <span className="font-bold tracking-wide">GLOBAL MYSTERY NIGHT — Dec 31</span>
        <span className="hidden sm:inline"> · One case, three waves, worldwide. </span>
        <span className="underline ml-2">Reserve your spot →</span>
      </Link>

      <nav className={`fixed top-[36px] left-0 right-0 z-50 transition-all ${scrolled ? "backdrop-blur-md bg-[#0a0908]/80 border-b border-[#2d2424]" : ""}`}>
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display text-xl tracking-wide" data-testid="brand-logo">
            <Wine size={18} className="text-[#8b0000]" />
            <span>wasntme<span className="text-[#8b0000]">.party</span></span>
            <span className="hidden sm:inline text-[10px] tracking-[0.2em] text-[#a39797] font-sans ml-1 border-l border-[#2d2424] pl-2">
              A NIGHTBLOOD CHRONICLES PRODUCTION
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/plan" className="btn-ghost text-sm hidden sm:inline-flex" data-testid="nav-plan">
              <Sparkles size={14} /> Plan my night
            </Link>
            <Link to="/daily" className="btn-ghost text-sm hidden sm:inline-flex" data-testid="nav-daily">
              <Flame size={14} /> Daily
            </Link>
            <Link to="/gifts" className="btn-ghost text-sm hidden sm:inline-flex" data-testid="nav-gifts">
              <Gift size={14} /> Gifts
            </Link>
            <Link to="/night" className="btn-ghost text-sm hidden sm:inline-flex" data-testid="nav-night">
              <Globe size={14} /> NYE Night
            </Link>
            <Link to="/make" className="btn-ghost text-sm hidden sm:inline-flex" data-testid="nav-make">
              <PenLine size={14} /> Make
            </Link>
            <Link to="/enterprise" className="btn-ghost text-sm hidden sm:inline-flex" data-testid="nav-enterprise">
              <Building2 size={14} /> For Organizations
            </Link>
            {user ? (
              <Link to="/my-kits" className="btn-ghost text-sm" data-testid="nav-my-kits">
                <User size={14} /> My Kits
              </Link>
            ) : (
              <button onClick={startGoogleSignIn} className="btn-ghost text-sm" data-testid="nav-signin">
                <LogIn size={14} /> Sign in
              </button>
            )}
            <a href="#cases" className="btn-primary text-sm" data-testid="nav-start-cta">
              <Skull size={16} /> Pick your case
            </a>
            <LanguageSwitcher compact />
          </div>
        </div>
      </nav>

      {/* NEW: Live Mystery Arena hero — the repositioned brand entry.
          Renders directly below the nav so Gen Z visitors see the Packs CTAs
          before the long-form marketing hero below. */}
      <div className="pt-40 relative" data-testid="arena-hero-wrap">
        <ArenaHero />
      </div>

      {/* Hero (legacy — kept below the fold for storytelling + SEO) */}
      <section className="pb-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-25 pointer-events-none"
          style={{ backgroundImage: `url(${heroImg})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(2px) grayscale(30%)" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0908]/50 to-[#0a0908]" />
        <div className="relative max-w-6xl mx-auto px-6">
          <SeasonBanner season={activeSeason} />
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl mt-8">
            <div className="font-hand text-[#d4af37] text-2xl rotate-[-2deg] mb-4" data-testid="hero-kicker">
              all-digital interactive mystery experiences. everyone's sus. even you.
            </div>
            <h1 className="font-display text-5xl sm:text-7xl font-bold leading-[0.95] tracking-tight"
              data-testid="hero-headline">
              {activeSeason?.hero_headline ? (
                <span style={{ color: "#f4f0ec" }}>{activeSeason.hero_headline}</span>
              ) : (
                <>Someone at your <span className="text-[#8b0000]">dinner party</span> is about to be murdered.</>
              )}
            </h1>
            <p className="mt-6 text-lg text-[#a39797] max-w-xl leading-relaxed">
              Not just a murder mystery. A world of interactive experiences: whodunnits, disappearances, hidden identities, betrayals, and team challenges. Tonight's featured case: you pay $35, your friends spend 60 seconds each answering three questions, and we hand you back a personalized mystery written by a human and starring everyone in your group chat.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="#cases" className="btn-primary" data-testid="hero-start-cta">
                <Skull size={18} /> Pick your case — $35
              </a>
              <a href="#how" className="btn-ghost text-sm" data-testid="hero-how-link">
                How it works
              </a>
              <Link to="/society" className="btn-ghost text-sm" data-testid="hero-society-cta">
                Join the Detective Society
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-[#a39797]">
              <span>Groups of 6–8</span>
              <span className="w-1 h-1 rounded-full bg-[#a39797]" />
              <span>Full refund before you lock</span>
              <span className="w-1 h-1 rounded-full bg-[#a39797]" />
              <span>Delivered to your inbox</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Platform positioning — one brand, many experiences */}
      <section className="py-14 border-t border-[#2d2424]" data-testid="platform-strip">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs tracking-widest text-[#d4af37] mb-2">ALL-DIGITAL INTERACTIVE MYSTERY EXPERIENCES</div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#f4f0ec] max-w-3xl">The mystery is the format. The experience is the product.</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {[
              { to: "/#cases", title: "Party & friends", body: "Personalized whodunnits, disappearances, hidden identities, and betrayals starring your group.", tid: "platform-party" },
              { to: "/streamer", title: "Streamers & creators", body: "Live social thrillers your chat can vote on, with overlays and challenges.", tid: "platform-streamers" },
              { to: "/enterprise", title: "Organizations", body: "Personalized Enterprise Experiences: structured team programs with facilitator guides and a Team Impact Report.", tid: "platform-enterprise" },
            ].map((c) => (
              <Link key={c.tid} to={c.to} className="card-noir p-6 block hover:border-[#8b0000] transition-colors" data-testid={c.tid}>
                <div className="font-display text-2xl text-[#f4f0ec]">{c.title}</div>
                <p className="mt-2 text-sm text-[#a39797] leading-relaxed">{c.body}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-[#d4af37]">Explore <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Founding Member banner — top of the sell funnel */}
      <section className="py-6 border-t border-[#2d2424] bg-gradient-to-r from-[#1a1413] to-[#0a0908]"
        data-testid="founder-banner">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-[280px]">
            <div className="text-xs tracking-widest text-[#d4af37] mb-1">LIMITED · 100 SLOTS · $99 ONE-TIME</div>
            <div className="font-display text-2xl sm:text-3xl text-[#f4f0ec]">Become a Founding Member.</div>
            <div className="text-sm text-[#a39797] mt-1">
              Lifetime access to every case we write. 25% off everything else. Forever.
            </div>
          </div>
          <Link to="/founder" className="btn-primary"
            style={{ backgroundColor: "#d4af37", color: "#0a0908" }}
            data-testid="landing-founder-cta">
            Claim my pass → $99
          </Link>
        </div>
      </section>

      {/* Teaser Vault ribbon — free play → email lead → 10% code */}
      <section className="py-5 border-t border-[#2d2424] bg-[#0a0908]"
        data-testid="teaser-banner">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-[260px]">
            <div className="text-xs tracking-widest text-[#d4af37] mb-1">FREE · HALLOWEEN TEASER VAULT</div>
            <div className="font-display text-xl sm:text-2xl text-[#f4f0ec]">
              Three bite-sized murders. Solve one, save 10% on any case.
            </div>
          </div>
          <Link to="/teaser" className="btn-ghost text-sm"
            data-testid="landing-teaser-cta">
            Play free → unlock code
          </Link>
        </div>
      </section>

      {/* Case Vault */}
      <section id="cases" className="py-20 relative border-t border-[#2d2424]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="font-hand text-[#d4af37] text-xl mb-2 rotate-[-1deg]">the case vault</div>
          <h2 className="font-display text-4xl sm:text-5xl mb-4">
            Pick your <span className="text-[#8b0000]">crime scene</span>.
          </h2>
          <p className="text-[#a39797] max-w-2xl mb-12">Every case is hand-written, personalized to your friend group, and delivered to your inbox after your friends submit. More cases drop every season.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {(() => {
              if (!activeSeason?.consumer_case_id) return cases;
              const featured = cases.find((c) => c.id === activeSeason.consumer_case_id);
              if (!featured) return cases;
              return [featured, ...cases.filter((c) => c.id !== activeSeason.consumer_case_id)];
            })().map((c, i) => (
              <CaseCard key={c.id} c={c} index={i} onComingSoon={setNotifyCase} playtestPromo={playtestPromo} />
            ))}
            {cases.length === 0 && (
              <div className="col-span-3 text-center text-[#a39797] py-12">Loading cases…</div>
            )}
          </div>
          <div className="mt-14 card-noir p-8 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#8b0000]/10 to-transparent pointer-events-none" />
            <div className="relative grid md:grid-cols-2 gap-6 items-center">
              <div>
                <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-2">the vault keeps growing</div>
                <h3 className="font-display text-3xl mb-2">Get the next case first.</h3>
                <p className="text-[#a39797]">New cases every season — weddings, Valentine's, holidays. Early-access emails hit your inbox the morning a new case drops. $5 off your first one.</p>
              </div>
              {newsletterDone ? (
                <div className="text-center" data-testid="newsletter-done">
                  <div className="font-display text-2xl mb-1 text-[#d4af37]">You're in.</div>
                  <div className="text-[#a39797] text-sm">We'll send the $5-off code when Case #006 lands.</div>
                </div>
              ) : (
                <form onSubmit={submitNewsletter} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
                  <input required type="email" className="input-noir flex-1"
                    placeholder="you@example.com"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    data-testid="newsletter-email" />
                  <button type="submit" className="btn-primary text-sm" data-testid="newsletter-submit">
                    <Mail size={14} /> Get early access
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* For Teams teaser (full page at /for-teams) */}
      <section id="for-teams" className="py-20 relative border-t border-[#2d2424]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="card-noir p-10 relative overflow-hidden" data-testid="for-teams-teaser">
            <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/15 via-transparent to-[#8b0000]/10 pointer-events-none" />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-2">for teams &amp; venues</div>
                <h2 className="font-display text-4xl sm:text-5xl mb-4">
                  Book a mystery for your <span className="text-[#8b0000]">company party</span>.
                </h2>
                <p className="text-[#a39797] mb-6">
                  Halloween, Christmas, New Year's, summer picnics, even inventory day. Instant download, no facilitator needed, safe for work, in person or virtual.
                </p>
                <Link to="/for-teams" className="btn-primary text-sm" data-testid="for-teams-cta">
                  <Building2 size={16} /> Plan a team event <ArrowRight size={14} />
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {packages.slice(0, 4).map((pkg) => (
                  <div key={pkg.id} className="border border-[#2d2424] rounded-lg p-3 text-center">
                    <div className="text-[10px] tracking-widest text-[#a39797] uppercase">{pkg.label}</div>
                    <div className="font-display text-xl text-[#f4f0ec] mt-1">{pkg.price_label}</div>
                    <div className="text-[10px] text-[#a39797] mt-1">{pkg.people}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How */}
      <section id="how" className="py-24 relative border-t border-[#2d2424]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="font-hand text-[#d4af37] text-xl mb-2 rotate-[-1deg]">the setup</div>
          <h2 className="font-display text-4xl sm:text-5xl mb-16">
            Four steps. One <span className="text-[#8b0000]">unforgettable</span> night.
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Wine, title: "Pay $35", desc: "Pick your case, date, and group size (6–8). You're the host." },
              { icon: PenLine, title: "Share the link", desc: "Each friend answers 3 questions about themselves. Takes 60 seconds." },
              { icon: Sparkles, title: "Assign roles", desc: "We generate the plot. You decide who plays the victim, the ex, the chaos agent." },
              { icon: Skull, title: "Print & play", desc: "Role cards, host script, clue sets, reveal — all in a PDF. Case Closed share card included." },
            ].map((s, i) => (
              <motion.div key={s.title} className="card-noir p-6"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }} data-testid={`how-step-${i + 1}`}>
                <div className="w-10 h-10 rounded-full bg-[#8b0000]/20 border border-[#8b0000] flex items-center justify-center mb-4">
                  <s.icon size={18} className="text-[#8b0000]" />
                </div>
                <div className="text-xs text-[#a39797] tracking-widest mb-2">STEP {i + 1}</div>
                <div className="font-display text-xl mb-2">{s.title}</div>
                <div className="text-sm text-[#a39797] leading-relaxed">{s.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Author */}
      <section className="py-24 border-t border-[#2d2424] relative overflow-hidden" data-testid="meet-the-author">
        <div className="absolute inset-0 bg-gradient-to-br from-[#8b0000]/10 via-transparent to-transparent pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-10 items-center">
            <div className="md:col-span-1">
              <div className="aspect-square rounded-2xl border-2 border-[#8b0000] bg-gradient-to-br from-[#2d2424] to-[#0a0908] flex items-center justify-center relative overflow-hidden">
                <div className="font-display text-6xl text-[#8b0000]">NB</div>
                <div className="absolute bottom-3 left-3 right-3 text-center">
                  <div className="font-hand text-[#d4af37] rotate-[-2deg] text-sm">a dark-fiction<br/>writer in your<br/>group chat</div>
                </div>
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-3">meet the author</div>
              <h2 className="font-display text-3xl sm:text-4xl mb-4">
                A dark-fiction writer is <span className="text-[#8b0000]">hosting your friends' murders.</span>
              </h2>
              <p className="text-[#a39797] leading-relaxed mb-4">
                NightBlood Chronicles writes gothic suspense — novels, short stories, and the hand-crafted mystery kits you're buying here. Every clue, red herring, and slip is written by a person who thinks about this stuff for a living. We don't fill your friends' names into a generic AI script. We write a case; the only thing we substitute from your friends' answers is their own volunteered names, signature lines, and harmless secrets.
              </p>
              <p className="text-[#a39797] leading-relaxed mb-6">
                If a case made your dinner party better, there are three ways to say so: tell a friend, pick up a NightBlood novel, or chip in to the author's crowdfund so the next case can be bigger than the last.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="https://gofund.me/70f3afe1f" target="_blank" rel="noopener noreferrer"
                  className="btn-primary text-sm" data-testid="author-gofundme-cta">
                  <Heart size={16} /> Support the author
                </a>
                <a href={AMAZON_URL} target="_blank" rel="noopener noreferrer"
                  onClick={(e) => { e.preventDefault(); setShowNovelTeaser(true); }}
                  className="btn-ghost text-sm relative" data-testid="author-novels-cta">
                  <span className="absolute -top-3 -right-3 rotate-[8deg] bg-[#8b0000] text-[#f4f0ec] text-[10px] font-bold tracking-[0.14em] uppercase px-2 py-[2px] rounded-sm shadow-[0_2px_6px_rgba(0,0,0,0.5)] border border-[#d4af37] flex items-center gap-1 whitespace-nowrap" data-testid="novels-just-released-ribbon">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37] animate-pulse" />
                    Just released
                  </span>
                  <Sparkles size={14} /> Read the novels
                </a>
                <a href="#cases" className="btn-ghost text-sm" data-testid="author-cases-cta">
                  <Skull size={14} /> Pick a case
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#2d2424] py-14 relative" data-testid="site-footer">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <div className="font-display text-2xl text-[#f4f0ec]">wasntme<span className="text-[#8b0000]">.party</span></div>
            <div className="mt-1 text-xs tracking-[0.2em] text-[#d4af37]">A NIGHTBLOOD CHRONICLES PRODUCTION</div>
            <div className="mt-4 text-sm text-[#a39797]">all-digital interactive mystery experiences for friends, streamers, and teams. Party cases for groups of 6 to 12, ages 18+</div>
            <div className="mt-2 text-xs tracking-[0.2em] text-[#d4af37]" data-testid="footer-veteran-owned">U.S. MARINE CORPS VETERAN-OWNED BUSINESS</div>
          </div>

          <div className="mt-10 pt-8 border-t border-[#2d2424] grid md:grid-cols-3 gap-8 text-sm">
            <div>
              <div className="text-xs tracking-widest text-[#d4af37] mb-3">FOLLOW @nightbloodchronicles</div>
              <div className="flex flex-wrap gap-3" data-testid="social-icons">
                <SocialLink href="https://www.tiktok.com/@nightbloodchronicles" label="TikTok" testid="social-tiktok">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.321 5.562a5.124 5.124 0 0 1-3.414-1.267 5.124 5.124 0 0 1-1.537-2.882V1.4h-3.004v12.19a3.12 3.12 0 0 1-5.606 1.882 3.12 3.12 0 0 1 2.496-4.966 3.1 3.1 0 0 1 .912.135V7.584a6.168 6.168 0 0 0-.912-.067 6.187 6.187 0 1 0 6.187 6.187V8.312a8.197 8.197 0 0 0 4.883 1.611V6.919a4.853 4.853 0 0 1-.005-1.357z"/></svg>
                </SocialLink>
                <SocialLink href="https://www.instagram.com/nightbloodchronicles" label="Instagram" testid="social-instagram">
                  <Instagram size={18} />
                </SocialLink>
                <SocialLink href="https://www.youtube.com/@nightbloodchronicles" label="YouTube" testid="social-youtube">
                  <Youtube size={18} />
                </SocialLink>
                <SocialLink href="https://www.linkedin.com/in/nightbloodchronicles" label="LinkedIn" testid="social-linkedin">
                  <Linkedin size={18} />
                </SocialLink>
                <SocialLink href="https://www.threads.net/@nightbloodchronicles" label="Threads" testid="social-threads">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.878-.76 2.116-1.207 3.578-1.291a13.19 13.19 0 0 1 2.973.183c-.126-.746-.379-1.339-.754-1.766-.516-.586-1.313-.88-2.371-.874-.025 0-.05 0-.075 0-.845 0-1.99.232-2.721 1.32L7.642 7.724c.98-1.454 2.568-2.256 4.464-2.256.025 0 .051 0 .076.001 3.17.02 5.058 1.957 5.245 5.422.107.064.213.13.316.197a7.21 7.21 0 0 1 2.573 2.75c.76 1.722.804 4.529-1.464 6.754-1.722 1.689-3.816 2.451-6.78 2.471Zm1.343-9.611c-.288 0-.579.009-.877.028-1.783.1-2.887.922-2.825 2.093.065 1.22 1.407 1.78 2.696 1.71 1.185-.065 2.727-.531 2.986-3.635a9.927 9.927 0 0 0-1.98-.196Z"/></svg>
                </SocialLink>
                <SocialLink href="https://www.pinterest.com/nightbloodchronicles" label="Pinterest" testid="social-pinterest">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741a.36.36 0 0 1 .083.345c-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.748-1.378l-.747 2.853c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>
                </SocialLink>
                <SocialLink href="https://discord.gg/nightbloodchronicles" label="Discord" testid="social-discord">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
                </SocialLink>
                <SocialLink href="https://www.snapchat.com/add/nightbloodchronicles" label="Snapchat" testid="social-snapchat">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.166.097C6.63.097 4.74 4.97 4.74 7.904c0 1.13.098 2.04.246 2.655-.37.197-.837.263-1.182.263-.616 0-1.133-.476-1.231-.772-.098-.246-.148-.346-.37-.346-.345 0-1.133.247-1.133.838 0 .64.912 1.33 1.7 1.577.246.098.69.148 1.034.247.345.098.69.197.936.345-.148.74-1.479 2.764-2.906 3.209-.246.098-.37.247-.37.493 0 .098 0 .247.1.395.37.74 1.823 1.133 3.154 1.33.049.148.147.69.246 1.034.098.345.148.59.59.59.246 0 .395-.048.838-.147.345-.098.838-.197 1.429-.197 1.034 0 1.577.197 2.07.59.542.395 1.034.74 2.07.74 1.085 0 1.577-.345 2.118-.74.493-.345 1.034-.59 2.07-.59.542 0 1.034.1 1.429.197.395.098.59.148.838.148.345 0 .493-.247.59-.59.098-.345.197-.837.246-1.035 1.33-.197 2.785-.59 3.155-1.33.098-.148.098-.296.098-.395 0-.246-.148-.395-.37-.493-1.428-.444-2.76-2.469-2.907-3.21.246-.147.59-.245.936-.344.345-.098.788-.148 1.034-.247.788-.247 1.7-.937 1.7-1.577 0-.59-.788-.838-1.133-.838-.222 0-.272.1-.37.346-.098.296-.615.772-1.231.772-.345 0-.838-.098-1.181-.263.147-.616.245-1.526.245-2.655 0-2.933-1.89-7.807-7.425-7.807z"/></svg>
                </SocialLink>
                <SocialLink href="https://linktr.ee/nightbloodchronicles" label="Linktree" testid="social-linktree">
                  <LinkIcon size={18} />
                </SocialLink>
              </div>
            </div>

            <div>
              <div className="text-xs tracking-widest text-[#d4af37] mb-3">GET IN TOUCH</div>
              <div className="space-y-2 text-[#a39797]">
                <a href="mailto:thenightbloodchronicles@gmail.com" className="block hover:text-[#f4f0ec] transition-colors" data-testid="footer-email">
                  <Mail size={14} className="inline mr-2" />thenightbloodchronicles@gmail.com
                </a>
                <a href="https://sites.google.com/view/nightbloodchronicles" target="_blank" rel="noopener noreferrer"
                  className="block hover:text-[#f4f0ec] transition-colors" data-testid="footer-google-site">
                  <LinkIcon size={14} className="inline mr-2" />nightbloodchronicles — official site
                </a>
                <a href={AMAZON_URL} target="_blank" rel="noopener noreferrer"
                  onClick={(e) => { e.preventDefault(); setShowNovelTeaser(true); }}
                  className="block hover:text-[#d4af37] transition-colors" data-testid="footer-novels">
                  <Sparkles size={14} className="inline mr-2" />Read the NightBlood novels
                  <span className="ml-2 inline-flex items-center gap-1 bg-[#8b0000] text-[#f4f0ec] text-[9px] font-bold tracking-[0.14em] uppercase px-1.5 py-[1px] rounded-sm border border-[#d4af37] align-middle" data-testid="footer-novels-ribbon">
                    <span className="w-1 h-1 rounded-full bg-[#d4af37] animate-pulse" />
                    Just released
                  </span>
                </a>
              </div>
            </div>

            <div>
              <div className="text-xs tracking-widest text-[#d4af37] mb-3">THE SITE</div>
              <div className="space-y-2 text-[#a39797]">
                <a href="#cases" className="block hover:text-[#f4f0ec] transition-colors">The Case Vault</a>
                <Link to="/for-teams" className="block hover:text-[#f4f0ec] transition-colors">For Teams &amp; Venues</Link>
                <Link to="/crowdfund" className="block hover:text-[#f4f0ec] transition-colors" data-testid="footer-crowdfund">
                  <Heart size={14} className="inline mr-1" />Back the crowdfund
                </Link>
                <a href="#how" className="block hover:text-[#f4f0ec] transition-colors">How it works</a>
                <Link to="/make" className="block hover:text-[#f4f0ec] transition-colors" data-testid="footer-make">
                  Build your own case · $12
                </Link>
                <Link to="/founder" className="block hover:text-[#f4f0ec] transition-colors text-[#d4af37]" data-testid="footer-founder">
                  Founding Member Pass · $99
                </Link>
                <Link to="/club" className="block hover:text-[#f4f0ec] transition-colors text-[#d4af37]" data-testid="footer-club">
                  Mystery Club · $9/mo
                </Link>
                <Link to="/teaser" className="block hover:text-[#f4f0ec] transition-colors text-[#d4af37]" data-testid="footer-teaser">
                  Free Teaser Vault · play & save 10%
                </Link>
                <Link to="/license" className="block hover:text-[#f4f0ec] transition-colors" data-testid="footer-license">
                  Venue &amp; Fundraiser Licenses
                </Link>
                <Link to="/commission" className="block hover:text-[#f4f0ec] transition-colors" data-testid="footer-commission">
                  Commission a mystery
                </Link>
                <Link to="/support" className="block hover:text-[#f4f0ec] transition-colors" data-testid="footer-support">
                  Help us grow
                </Link>
                <Link to="/library" className="block hover:text-[#f4f0ec] transition-colors" data-testid="footer-library">
                  My Library
                </Link>
                <Link to="/admin" className="block hover:text-[#f4f0ec] transition-colors text-xs">Admin</Link>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-[#2d2424] flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#a39797]">
            <div>© 2026 NightBlood Chronicles · wasntme.party</div>
            <div className="flex gap-4" data-testid="footer-legal">
              <Link to="/terms" className="hover:text-[#f4f0ec]" data-testid="footer-terms">Terms</Link>
              <Link to="/privacy" className="hover:text-[#f4f0ec]" data-testid="footer-privacy">Privacy</Link>
              <Link to="/refunds" className="hover:text-[#f4f0ec]" data-testid="footer-refunds">Refunds</Link>
            </div>
          </div>
        </div>
      </footer>
      {notifyCase && <NotifyModal caseInfo={notifyCase} onClose={() => setNotifyCase(null)} />}
      {inquiryParty && <InquiryModal initialParty={inquiryParty} onClose={() => setInquiryParty(null)} />}
      {showNovelTeaser && <NovelTeaserModal onClose={() => setShowNovelTeaser(false)} />}
    </div>
  );
};

export default Landing;
